import * as React from 'react';

import { inputBaseClasses } from '@mui/material/InputBase';
import { styled } from '@mui/material/styles';
import composeClasses from '@mui/utils/composeClasses';
import useLazyRef from '@mui/utils/useLazyRef';
import type {
  GridColumnVisibilityModel,
  GridColDef,
  GridSlotProps,
} from '@mui/x-data-grid';
import {
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  getDataGridUtilityClass,
} from '@mui/x-data-grid';
import type { DataGridProcessedProps } from '@mui/x-data-grid/models/props/DataGridProps';

import type { ButtonProps } from '../../button';
import { FormControlLabel } from '../../form-control-label';
import { Stack } from '../../stack';
import { TextField } from '../../text-field';

import {
  checkColumnVisibilityModelsSame,
  defaultSearchPredicate,
} from './utils';

// This is a fork of https://github.com/mui/mui-x/blob/master/packages/x-data-grid/src/components/columnsManagement/GridColumnsManagement.tsx
// Note: This implementation relies heavily on internal APIs, which may cause issues when updating MUI.
// forked from package version: 7.23.6

// Notable changes:
// - Allow custom logic to be passed in with: toggleColumn, columnVisibility, onColumnVisibilityChange, actionButtonProps
// - Include an actionButton
// - Include a max prop to restrict amount of changes at one time
// - Include a defaultColumnVisibilityModel prop that contains the original columnVisibility set; replaces initialColumnVisibilityModel if set

// custom logic start
type MuiGridColumnsManagementProps = GridSlotProps['columnsManagement'];

interface GridColumnsManagementProps extends MuiGridColumnsManagementProps {
  toggleColumn: (event: React.MouseEvent<HTMLButtonElement>) => void;
  columnVisibility: GridColumnVisibilityModel;
  onColumnVisibilityChange: (model: GridColumnVisibilityModel) => void;
  actionButtonProps?: ButtonProps & { text: string };
}
// custom logic end

type OwnerState = DataGridProcessedProps;

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['columnsManagement'],
    header: ['columnsManagementHeader'],
    searchInput: ['columnsManagementSearchInput'],
    footer: ['columnsManagementFooter'],
    row: ['columnsManagementRow'],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const collator = new Intl.Collator();

function GridColumnsManagement(props: GridColumnsManagementProps) {
  const apiRef = useGridApiContext();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  const rootProps = useGridRootProps();
  const [searchValue, setSearchValue] = React.useState('');
  const classes = useUtilityClasses(rootProps);

  const {
    sort,
    searchPredicate = defaultSearchPredicate,
    autoFocusSearchField = true,
    disableShowHideToggle = false,
    disableResetButton = false,
    toggleAllMode = 'all',
    getTogglableColumns,
    searchInputProps,
    // custom logic start
    actionButtonProps,
    defaultColumnVisibilityModel,
    columnVisibility,
    onColumnVisibilityChange,
    max,
    toggleColumn,
    // custom logic end
  } = props;

  // custom logic start
  const initialColumnVisibilityModel = useLazyRef(
    () =>
      defaultColumnVisibilityModel ?? gridColumnVisibilityModelSelector(apiRef),
    // Typing invalid after updating to v6. Should not be returning GridColumnVisibilityModel or null. Issue with MUI file as well.
  ).current as GridColumnVisibilityModel;

  const visibleColumnCount = React.useMemo(
    () => Object.values(columnVisibility).filter((value) => value).length,
    [columnVisibility],
  );

  const maxColumnReached = max ? visibleColumnCount >= max : false;

  if (max && visibleColumnCount > max) {
    // eslint-disable-next-line no-console
    console.error(
      [
        `Fenrir: The number of visible columns (${visibleColumnCount}) exceeds the maximum allowed (${max}).`,
        `Please make sure that the visible columns do not exceed the maximum allowed`,
      ].join('\n'),
    );
  }
  // custom logic end

  const isResetDisabled = React.useMemo(
    () =>
      checkColumnVisibilityModelsSame(
        columnVisibility,
        initialColumnVisibilityModel,
      ),
    [columnVisibility, initialColumnVisibilityModel],
  );

  const sortedColumns = React.useMemo(() => {
    switch (sort) {
      case 'asc':
        return [...columns].sort((a, b) =>
          collator.compare(a.headerName || a.field, b.headerName || b.field),
        );

      case 'desc':
        return [...columns].sort(
          (a, b) =>
            -collator.compare(a.headerName || a.field, b.headerName || b.field),
        );

      default:
        return columns;
    }
  }, [columns, sort]);

  const currentColumns = React.useMemo(() => {
    const togglableColumns = getTogglableColumns
      ? getTogglableColumns(sortedColumns)
      : null;

    const togglableSortedColumns = togglableColumns
      ? sortedColumns.filter(({ field }) => togglableColumns.includes(field))
      : sortedColumns;

    if (!searchValue) {
      return togglableSortedColumns;
    }

    return togglableSortedColumns.filter((column) =>
      searchPredicate(column, searchValue.toLowerCase()),
    );
  }, [sortedColumns, searchValue, searchPredicate, getTogglableColumns]);

  const toggleAllColumns = React.useCallback(
    (isVisible: boolean) => {
      const currentModel = gridColumnVisibilityModelSelector(apiRef);
      const newModel = { ...currentModel };
      const togglableColumns = getTogglableColumns
        ? getTogglableColumns(columns)
        : null;

      (toggleAllMode === 'filteredOnly' ? currentColumns : columns).forEach(
        (col) => {
          if (
            col.hideable &&
            (togglableColumns == null || togglableColumns.includes(col.field))
          ) {
            // * Note: Instead of deleting keys from the visibility model, we set their values to `true` or `false` from isVisible.
            // * This approach ensures that the model remains consistent and avoids potential issues with undefined keys,
            // * which could lead to unexpected behavior in the grid's state management.
            newModel[col.field] = isVisible;
          }
        },
      );

      return onColumnVisibilityChange(newModel);
    },
    [
      apiRef,
      columns,
      getTogglableColumns,
      toggleAllMode,
      currentColumns,
      onColumnVisibilityChange,
    ],
  );

  const handleSearchValueChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [],
  );

  const hideableColumns = React.useMemo(
    () => currentColumns.filter((col) => col.hideable),
    [currentColumns],
  );

  const allHideableColumnsVisible = React.useMemo(
    () =>
      hideableColumns.every(
        (column) =>
          columnVisibility[column.field] == null ||
          columnVisibility[column.field] !== false,
      ),
    [columnVisibility, hideableColumns],
  );

  const allHideableColumnsHidden = React.useMemo(
    () =>
      hideableColumns.every(
        (column) => columnVisibility[column.field] === false,
      ),
    [columnVisibility, hideableColumns],
  );

  const firstSwitchRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (autoFocusSearchField) {
      searchInputRef.current?.focus();
    } else if (
      firstSwitchRef.current &&
      typeof firstSwitchRef.current.focus === 'function'
    ) {
      firstSwitchRef.current.focus();
    }
  }, [autoFocusSearchField]);

  let firstHideableColumnFound = false;
  const isFirstHideableColumn = (column: GridColDef) => {
    if (firstHideableColumnFound === false && column.hideable !== false) {
      firstHideableColumnFound = true;
      return true;
    }
    return false;
  };
  const handleSearchReset = React.useCallback(() => {
    setSearchValue('');
    searchInputRef.current?.focus();
  }, []);

  return (
    <React.Fragment>
      <GridColumnsManagementHeader
        className={classes.header}
        ownerState={rootProps}
      >
        <SearchInput
          as={rootProps.slots.baseTextField}
          ownerState={rootProps}
          placeholder={apiRef.current.getLocaleText(
            'columnsManagementSearchTitle',
          )}
          inputRef={searchInputRef}
          className={classes.searchInput}
          value={searchValue}
          onChange={handleSearchValueChange}
          variant="outlined"
          size="small"
          type="search"
          InputProps={{
            startAdornment: (
              <rootProps.slots.baseInputAdornment position="start">
                <rootProps.slots.quickFilterIcon />
              </rootProps.slots.baseInputAdornment>
            ),
            endAdornment: (
              <rootProps.slots.baseIconButton
                aria-label={apiRef.current.getLocaleText(
                  'columnsManagementDeleteIconLabel',
                )}
                size="small"
                sx={[
                  searchValue
                    ? {
                        visibility: 'visible',
                      }
                    : {
                        visibility: 'hidden',
                      },
                ]}
                tabIndex={-1}
                onClick={handleSearchReset}
                {...rootProps.slotProps?.baseIconButton}
              >
                <rootProps.slots.quickFilterClearIcon fontSize="small" />
              </rootProps.slots.baseIconButton>
            ),
          }}
          inputProps={{
            'aria-label': apiRef.current.getLocaleText(
              'columnsManagementSearchTitle',
            ),
          }}
          autoComplete="off"
          fullWidth
          {...rootProps.slotProps?.baseTextField}
          {...searchInputProps}
        />
      </GridColumnsManagementHeader>
      <GridColumnsManagementBody
        className={classes.root}
        ownerState={rootProps}
      >
        {currentColumns.map((column) => {
          return (
            <FormControlLabel
              key={column.field}
              className={classes.row}
              control={
                <rootProps.slots.baseCheckbox
                  disabled={
                    column.hideable === false ||
                    (columnVisibility[column.field] === false &&
                      maxColumnReached)
                  }
                  checked={columnVisibility[column.field] !== false}
                  onClick={toggleColumn}
                  name={column.field}
                  sx={{ p: 0.5 }}
                  inputRef={
                    isFirstHideableColumn(column) ? firstSwitchRef : undefined
                  }
                  {...rootProps.slotProps?.baseCheckbox}
                />
              }
              label={column.headerName || column.field}
            />
          );
        })}
        {currentColumns.length === 0 && (
          <GridColumnsManagementEmptyText ownerState={rootProps}>
            {apiRef.current.getLocaleText('columnsManagementNoColumns')}
          </GridColumnsManagementEmptyText>
        )}
      </GridColumnsManagementBody>
      {/* custom logic: actionButtonProps */}
      {(!disableShowHideToggle || !disableResetButton || actionButtonProps) &&
      currentColumns.length > 0 ? (
        <GridColumnsManagementFooter
          ownerState={rootProps}
          className={classes.footer}
        >
          {!disableShowHideToggle ? (
            <FormControlLabel
              control={
                <rootProps.slots.baseCheckbox
                  disabled={hideableColumns.length === 0}
                  checked={allHideableColumnsVisible}
                  indeterminate={
                    !allHideableColumnsVisible && !allHideableColumnsHidden
                  }
                  onClick={() => toggleAllColumns(!allHideableColumnsVisible)}
                  name={apiRef.current.getLocaleText(
                    'columnsManagementShowHideAllText',
                  )}
                  sx={{ p: 0.5 }}
                  {...rootProps.slotProps?.baseCheckbox}
                />
              }
              label={apiRef.current.getLocaleText(
                'columnsManagementShowHideAllText',
              )}
            />
          ) : (
            <span />
          )}

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {!disableResetButton ? (
              <rootProps.slots.baseButton
                onClick={() => {
                  onColumnVisibilityChange(initialColumnVisibilityModel);
                }}
                size="small"
                disabled={isResetDisabled}
                {...rootProps.slotProps?.baseButton}
              >
                {apiRef.current.getLocaleText('columnsManagementReset')}
              </rootProps.slots.baseButton>
            ) : null}

            {/* custom logic start */}
            {actionButtonProps ? (
              <rootProps.slots.baseButton
                variant="contained"
                size="small"
                {...rootProps.slotProps?.baseButton}
                {...actionButtonProps}
              >
                {actionButtonProps.text}
              </rootProps.slots.baseButton>
            ) : null}
            {/* custom logic end */}
          </Stack>
        </GridColumnsManagementFooter>
      ) : null}
    </React.Fragment>
  );
}

const GridColumnsManagementBody = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ColumnsManagement',
  overridesResolver: (props, styles) => styles.columnsManagement,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  padding: theme.spacing(0, 3, 1.5),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  flex: '1 1',
  maxHeight: 400,
  alignItems: 'flex-start',
}));

const GridColumnsManagementHeader = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ColumnsManagementHeader',
  overridesResolver: (props, styles) => styles.columnsManagementHeader,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
}));

const SearchInput = styled(TextField, {
  name: 'MuiDataGrid',
  slot: 'ColumnsManagementSearchInput',
  overridesResolver: (props, styles) => styles.columnsManagementSearchInput,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  [`& .${inputBaseClasses.root}`]: {
    padding: theme.spacing(0, 1.5, 0, 1.5),
  },
  [`& .${inputBaseClasses.input}::-webkit-search-decoration,
  & .${inputBaseClasses.input}::-webkit-search-cancel-button,
  & .${inputBaseClasses.input}::-webkit-search-results-button,
  & .${inputBaseClasses.input}::-webkit-search-results-decoration`]: {
    /* clears the 'X' icon from Chrome */
    display: 'none',
  },
}));

const GridColumnsManagementFooter = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ColumnsManagementFooter',
  overridesResolver: (props, styles) => styles.columnsManagementFooter,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  padding: theme.spacing(0.5, 1, 0.5, 3),
  display: 'flex',
  justifyContent: 'space-between',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const GridColumnsManagementEmptyText = styled('div')<{
  ownerState: OwnerState;
}>(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
  color: theme.palette.grey[500],
}));

export { GridColumnsManagement };
